import React from "react";
import CrudPage, { Badge } from "@/components/CrudPage";
import { formatMoney, formatDate } from "@/lib/format";
import { CheckCircle2 } from "lucide-react";
import ProductionListButton from "@/components/ProductionListButton";

const STAGES = ["Upit", "Ponuda", "Dogovoreno", "U izradi", "Završeno"];
const stageTone = { Upit: "muted", Ponuda: "info", Dogovoreno: "warning", "U izradi": "warning", "Završeno": "success" };
const COUNTRIES = ["Bosna i Hercegovina", "Slovenija", "Austrija", "Njemačka", "Ostalo"];
const TYPES = ["Firma", "Fizičko lice"];
const AMOUNT_TYPES = ["Neto", "Bruto (sa PDV)"];
const VAT = { "Bosna i Hercegovina": 0.17, "Slovenija": 0.22, "Austrija": 0.20, "Njemačka": 0.19, "Ostalo": 0 };

function Tile({ label, value, accent }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-extrabold font-heading tabular-nums mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}

export default function Deals() {
  const compute = (r) => {
    const currency = r.country === "Bosna i Hercegovina" ? "KM" : "EUR";
    const amount = Number(r.amount) || 0;
    const advance = Number(r.advance) || 0;
    const isBruto = r.amount_type === "Bruto (sa PDV)";
    const vat = VAT[r.country] ?? 0;
    const net = isBruto ? Math.round((amount / (1 + vat)) * 100) / 100 : amount;
    const netDebt = Math.max(0, Math.round((net - advance) * 100) / 100);
    const out = Math.max(0, Math.round((amount - advance) * 100) / 100);
    let delivery = "";
    if (r.date_agreed && r.weeks) {
      const d = new Date(r.date_agreed);
      if (!isNaN(d.getTime())) {
        d.setUTCDate(d.getUTCDate() + Number(r.weeks) * 7);
        delivery = d.toISOString().slice(0, 10);
      }
    }
    return { ...r, _currency: currency, _amount: amount, _advance: advance, _debt: netDebt, _netDebt: netDebt, _out: out, _delivery: delivery, _net: net, _isBruto: isBruto, _vat: vat, _transport: !!r.transport, _invoiced: !!r.invoiced, _group: `${r.country || "Ostalo"} — ${r.invoiced ? "Fakturisano" : "U realizaciji"}` };
  };

  const countryRank = { "Bosna i Hercegovina": 0, "Slovenija": 1, "Austrija": 2, "Njemačka": 3, "Ostalo": 4 };
  const sortRows = (a, b) => {
    const cr = (countryRank[a.country] ?? 9) - (countryRank[b.country] ?? 9);
    if (cr) return cr;
    const ai = a.invoiced ? 1 : 0;
    const bi = b.invoiced ? 1 : 0;
    if (ai !== bi) return ai - bi;
    const an = (a.company_name || a.customer_name || "").toLowerCase();
    const bn = (b.company_name || b.customer_name || "").toLowerCase();
    if (an < bn) return -1;
    if (an > bn) return 1;
    const ad = a._delivery || "9999-99-99";
    const bd = b._delivery || "9999-99-99";
    return ad < bd ? -1 : ad > bd ? 1 : 0;
  };

  const columns = [
    { key: "customer_type", label: "Tip", render: (r) => <Badge tone={r.customer_type === "Firma" ? "info" : "muted"}>{r.customer_type || "—"}</Badge> },
    { key: "company_name", label: "Firma", render: (r) => <span className="font-medium">{r.company_name || "—"}</span> },
    { key: "customer_name", label: "Kupac (fizičko lice)", render: (r) => r.customer_name || "—" },
    { key: "title", label: "Posao", render: (r) => (
      <div>
        <span>{r.title}</span>
        <div className="flex gap-1 mt-1 flex-wrap">
          {r._transport ? <Badge tone="info">Transport</Badge> : null}
          {r._invoiced ? <Badge tone="warning">Fakturisano</Badge> : null}
        </div>
      </div>
    ) },
    { key: "date_agreed", label: "Dogovoreno", render: (r) => formatDate(r.date_agreed), export: (r) => r.date_agreed },
    { key: "_delivery", label: "Rok isporuke", render: (r) => <span className="whitespace-nowrap">{formatDate(r._delivery)} {r.weeks ? <span className="text-xs text-muted-foreground">({r.weeks} sed.)</span> : null}</span>, export: (r) => r._delivery },
    { key: "_amount", label: "Iznos (ugovoreni)", render: (r) => <span className="tabular-nums font-semibold">{formatMoney(r._amount, r._currency)} <span className="text-xs text-muted-foreground font-normal">({r._isBruto ? "bruto" : "neto"})</span></span>, export: (r) => r._amount },
    { key: "_net", label: "Neto (bez PDV)", render: (r) => <span className="tabular-nums font-semibold text-emerald-700 dark:text-emerald-400">{formatMoney(r._net, r._currency)}{r._isBruto ? <span className="block text-xs text-muted-foreground font-normal">PDV {Math.round(r._vat * 100)}%</span> : null}</span>, export: (r) => r._net },
    { key: "_advance", label: "Avans", render: (r) => <span className="tabular-nums">{formatMoney(r._advance, r._currency)}</span>, export: (r) => r._advance },
    { key: "_debt", label: "Dug (neto)", render: (r) => <span className={`tabular-nums font-semibold ${r._netDebt > 0 ? "text-amber-600" : "text-emerald-600"}`}>{formatMoney(r._netDebt, r._currency)}</span>, export: (r) => r._netDebt },
    { key: "stage", label: "Status", render: (r) => <Badge tone={stageTone[r.stage] || "muted"}>{r.stage}</Badge> },
  ];

  const fields = [
    {
      key: "_picker", label: "Odaberi spremljenog kupca (imenik)", type: "directory", full: true,
      directory: {
        endpoint: "customers",
        placeholder: "Odaberi kupca iz imenika...",
        groupBy: "country",
        getLabel: (c) => `${c.company_name || c.customer_name || "—"}${c.contact_phone ? " · " + c.contact_phone : ""}`,
        createFields: [
          { key: "customer_type", label: "Tip kupca", type: "select", options: TYPES },
          { key: "company_name", label: "Naziv firme (ako je firma)" },
          { key: "customer_name", label: "Ime kupca / kontakt osoba" },
          { key: "country", label: "Zemlja", type: "select", options: COUNTRIES },
          { key: "contact_phone", label: "Telefon" },
        ],
        map: (c) => ({ customer_type: c.customer_type, company_name: c.company_name, customer_name: c.customer_name, country: c.country, contact_phone: c.contact_phone }),
      },
    },
    { key: "customer_type", label: "Tip kupca", type: "select", options: TYPES, required: true },
    { key: "country", label: "Zemlja (određuje valutu)", type: "select", options: COUNTRIES, required: true },
    { key: "company_name", label: "Naziv firme (ako je firma)" },
    { key: "customer_name", label: "Ime kupca / kontakt osoba" },
    { key: "title", label: "Opis posla", full: true, required: true },
    { key: "date_agreed", label: "Datum dogovora", type: "date" },
    { key: "weeks", label: "Rok isporuke (broj sedmica)", type: "number" },
    { key: "amount", label: "Iznos", type: "number", step: "0.01" },
    { key: "amount_type", label: "Vrsta iznosa (Firma = Neto, Fizičko lice = Bruto sa PDV)", type: "select", options: AMOUNT_TYPES },
    { key: "advance", label: "Uplaćen avans", type: "number", step: "0.01" },
    { key: "stage", label: "Status", type: "select", options: STAGES },
    { key: "invoiced", label: "Fakturisano kupcu", type: "checkbox", checkboxLabel: "Poslali smo fakturu, čekamo naplatu" },
    { key: "transport", label: "Transport (evidencija)", type: "checkbox", checkboxLabel: "Transport — NE ulazi u neto zbir (samo evidencija; štrihiraj kad kupac uplati)" },
    { key: "contact_phone", label: "Telefon" },
    { key: "note", label: "Napomena", type: "textarea", full: true },
  ];

  const rowActions = [
    {
      key: "done",
      label: "Gotovo",
      icon: CheckCircle2,
      tone: "success",
      successText: "Posao završen — dug 0,00",
      visible: (r) => r.stage !== "Završeno" || r._debt > 0,
      patch: (r) => ({ stage: "Završeno", advance: Number(r.amount) || 0 }),
    },
  ];

  const summary = (rows) => {
    const sum = (arr, k) => arr.reduce((a, b) => a + (Number(b[k]) || 0), 0);
    const nonT = rows.filter((r) => !r._transport);
    const km = nonT.filter((r) => r._currency === "KM");
    const eur = nonT.filter((r) => r._currency === "EUR");
    const realKm = km.filter((r) => !r._invoiced);
    const realEur = eur.filter((r) => !r._invoiced);
    const invKm = km.filter((r) => r._invoiced && r._netDebt > 0);
    const invEur = eur.filter((r) => r._invoiced && r._netDebt > 0);
    const tKm = rows.filter((r) => r._transport && r._currency === "KM");
    const tEur = rows.filter((r) => r._transport && r._currency === "EUR");
    const countries = [["Slovenija", "slovenija"], ["Austrija", "austrija"], ["Njemačka", "njemacka"]];
    return (
      <div className="space-y-3">
      <div className="flex justify-end"><ProductionListButton compute={compute} /></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="deals-country-overview">
        {countries.map(([c, id]) => {
          const list = realEur.filter((r) => r.country === c);
          return (
            <div key={c} className="rounded-xl border bg-card p-4" data-testid={`country-card-${id}`}>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">{c} — nalozi za realizaciju</div>
              <div className="mt-2 flex items-baseline justify-between gap-2">
                <span className="text-2xl font-extrabold font-heading tabular-nums" data-testid={`country-count-${id}`}>{list.length} {list.length === 1 ? "nalog" : "naloga"}</span>
                <span className="text-lg font-extrabold font-heading tabular-nums text-emerald-700 dark:text-emerald-400" data-testid={`country-net-${id}`}>{formatMoney(sum(list, "_net"), "EUR")}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="deals-summary">
        <Tile label="Ukupno poslova" value={rows.length} />
        <Tile label="Neto zbir EUR (za realizaciju)" value={formatMoney(sum(realEur, "_net"), "EUR")} accent="text-emerald-700 dark:text-emerald-400" />
        <Tile label="Avans za realizaciju EUR" value={formatMoney(sum(realEur, "_advance"), "EUR")} accent="text-indigo-600" />
        <Tile label="Neto dug EUR" value={formatMoney(sum(realEur, "_netDebt"), "EUR")} accent="text-amber-600" />
        <Tile label="Neto naplata fakturisano EUR" value={formatMoney(sum(invEur, "_netDebt"), "EUR")} accent="text-blue-600" />
        <Tile label="Neto zbir BiH (za realizaciju)" value={formatMoney(sum(realKm, "_net"), "KM")} accent="text-emerald-700 dark:text-emerald-400" />
        <Tile label="Avans za realizaciju BiH" value={formatMoney(sum(realKm, "_advance"), "KM")} accent="text-indigo-600" />
        <Tile label="Neto dug BiH" value={formatMoney(sum(realKm, "_netDebt"), "KM")} accent="text-amber-600" />
        <Tile label="Neto naplata fakturisano BiH" value={formatMoney(sum(invKm, "_netDebt"), "KM")} accent="text-blue-600" />
        <Tile label="Transport evidencija (EUR / KM)" value={`${formatMoney(sum(tEur, "_out"), "EUR")} / ${formatMoney(sum(tKm, "_out"), "KM")}`} accent="text-slate-600 dark:text-slate-300" />
      </div>
      </div>
    );
  };

  return (
    <CrudPage
      title="Kupci & Poslovi"
      subtitle="Poslovi grupisani po zemlji, sortirani po firmi i roku isporuke. Neto zbir ne uključuje Transport."
      endpoint="deals"
      columns={columns}
      fields={fields}
      testid="deals"
      computeRow={compute}
      emptyDefaults={{ customer_type: "Firma", country: "Slovenija", stage: "Dogovoreno", advance: 0, amount_type: "Neto", invoiced: false, transport: false }}
      filterOptions={{ key: "country", label: "Zemlja", options: COUNTRIES }}
      summary={summary}
      rowActions={rowActions}
      groupBy="_group"
      sortRows={sortRows}
    />
  );
}
